<template>
  <div class="bb-bidding">
    <BbScoreBar />

    <div class="content">
      <div class="top-row">
        <header class="round-head">
          <h1 class="title">Ronde {{ activeRoundNumber }} <span class="of">van {{ totalRounds }}</span></h1>
          <p class="subtitle">{{ activeCards }} {{ activeCards === 1 ? 'kaart' : 'kaarten' }} per speler</p>
        </header>

        <section class="card trump-card">
          <h2 class="card-title">Troef</h2>
          <div class="trumps" role="radiogroup" aria-label="Troefkleur">
            <button
              v-for="option in trumpOptions"
              :key="option.id"
              type="button"
              class="trump-btn"
              :class="{ selected: trump === option.id, red: option.red }"
              role="radio"
              :aria-checked="trump === option.id"
              :aria-label="option.label"
              @click="setTrump(option.id)"
            >
              <span class="trump-symbol">{{ option.symbol }}</span>
              <span class="trump-label">{{ option.label }}</span>
            </button>
          </div>
        </section>
      </div>

      <BbPlayerGrid class="bids" :count="biddingOrder.length">
        <section
          v-for="(player, index) in biddingOrder"
          :key="player.id"
          class="card bid-card"
          :class="{ pending: bids[player.id] === null || bids[player.id] === undefined }"
        >
          <div class="bid-head">
            <span class="bid-order">{{ index + 1 }}</span>
            <span class="bid-name">{{ player.name }}</span>
            <span v-if="player.id === activeDealerId" class="dealer-tag">Deler</span>
            <span class="bid-value">{{ bids[player.id] ?? '–' }}</span>
          </div>
          <BbNumberPicker
            :model-value="bids[player.id] ?? null"
            :max="activeCards"
            :blocked-value="player.id === activeDealerId ? forbiddenForDealer : null"
            blocked-hint="Met dit bod zou de som gelijk zijn aan het aantal slagen"
            :aria-label="'Bod van ' + player.name"
            @update:model-value="setBid(player.id, $event)"
          />
          <p v-if="player.id === activeDealerId && forbiddenForDealer !== null" class="blocked-note">
            {{ forbiddenForDealer }} kan niet: de som van de biedingen mag niet gelijk zijn aan {{ activeCards }}.
          </p>
        </section>
      </BbPlayerGrid>

      <p class="total-line" :class="{ warn: allBidsPlaced && totalBid === activeCards }">
        Totaal geboden: <strong>{{ totalBid }}</strong> van {{ activeCards }}
        <span v-if="allBidsPlaced" class="total-hint">
          ({{ totalBid > activeCards ? 'overboden' : totalBid < activeCards ? 'onderboden' : 'niet toegestaan' }})
        </span>
      </p>
    </div>

    <AppFooter
      :can-continue="canContinue"
      :show-back="true"
      @continue="submit"
      @back="cancel"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useBoerenbridgeStore } from '../../stores/boerenbridge'
import { TRUMP_OPTIONS, forbiddenBid } from '../../models/boerenbridge'
import BbScoreBar from './BbScoreBar.vue'
import BbNumberPicker from './BbNumberPicker.vue'
import BbPlayerGrid from './BbPlayerGrid.vue'
import AppFooter from '../shared/AppFooter.vue'

export default {
  name: 'BbBidding',
  components: { BbScoreBar, BbNumberPicker, BbPlayerGrid, AppFooter },
  data() {
    return { trumpOptions: TRUMP_OPTIONS }
  },
  created() {
    this.ensureRoundDraft()
  },
  computed: {
    ...mapState(useBoerenbridgeStore, [
      'players',
      'biddingOrder',
      'activeRoundNumber',
      'activeCards',
      'activeDealerId',
      'totalRounds',
      'currentBid',
      'editingRoundNumber'
    ]),
    // Bod en troef leven in de store, zodat ze bewaard blijven bij terugnavigeren
    trump() {
      return this.currentBid?.trump ?? null
    },
    bids() {
      return this.currentBid?.bids ?? {}
    },
    otherBids() {
      return this.biddingOrder
        .filter(p => p.id !== this.activeDealerId)
        .map(p => this.bids[p.id])
    },
    /** Pas blokkeren wanneer alle andere spelers geboden hebben. */
    forbiddenForDealer() {
      if (this.otherBids.some(bid => bid === null || bid === undefined)) return null
      return forbiddenBid(this.activeCards, this.otherBids)
    },
    allBidsPlaced() {
      return this.players.every(p => this.bids[p.id] !== null && this.bids[p.id] !== undefined)
    },
    totalBid() {
      return this.players.reduce((sum, p) => sum + (this.bids[p.id] ?? 0), 0)
    },
    canContinue() {
      return this.trump !== null && this.allBidsPlaced && this.totalBid !== this.activeCards
    }
  },
  watch: {
    /** Een eerder bod van de deler kan door latere wijzigingen ongeldig worden. */
    forbiddenForDealer(value) {
      if (value !== null && this.bids[this.activeDealerId] === value) {
        this.setBid(this.activeDealerId, null)
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    ...mapActions(useBoerenbridgeStore, [
      'ensureRoundDraft',
      'setTrump',
      'setBid',
      'goToOverview',
      'cancelRound',
      'cancelEdit'
    ]),
    submit() {
      if (!this.canContinue) return
      this.goToOverview()
    },
    cancel() {
      this.editingRoundNumber === null ? this.cancelRound() : this.cancelEdit()
    },
    handleKeydown(e) {
      if (e.key === 'Enter' && this.canContinue) this.submit()
      if (e.key === 'Escape') this.cancel()
    }
  }
}
</script>

<style scoped>
.bb-bidding {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.top-row {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.round-head {
  text-align: center;
}

.title {
  font-size: 1.5rem;
  color: #2c3e50;
}

.of {
  font-size: 1rem;
  font-weight: 400;
  color: #5a6d5a;
}

.subtitle {
  font-size: 0.9rem;
  color: #5a6d5a;
}

.card {
  width: 100%;
  max-width: min(560px, 100%);
  background: #fff;
  border: 2px solid #aac8aa;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.card.pending {
  border-style: dashed;
}

.card-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #2c3e50;
  opacity: 0.6;
}

.trumps {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.trump-btn {
  flex: 1 1 4rem;
  min-height: 3.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  padding: 0.35rem 0.3rem;
  font-family: inherit;
  color: #2c3e50;
  background: #fff;
  border: 2px solid #aac8aa;
  border-radius: 8px;
  cursor: pointer;
  touch-action: manipulation;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.trump-symbol {
  font-size: 1.35rem;
  line-height: 1;
}

.trump-btn.red .trump-symbol { color: #c0392b; }

.trump-label {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.trump-btn.selected {
  background: #2c3e50;
  border-color: #2c3e50;
  color: #fff;
}

.trump-btn.selected.red .trump-symbol { color: #ff9f94; }

.trump-btn:active { opacity: 0.8; }

.bid-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bid-order {
  flex: 0 0 auto;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #7a9a7a;
  background: #eef5ee;
  border-radius: 50%;
}

.bid-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dealer-tag {
  flex: 0 0 auto;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #fff;
  background: #2c3e50;
  border-radius: 999px;
  padding: 0.15rem 0.45rem;
}

.bid-value {
  margin-left: auto;
  font-size: 1.35rem;
  font-weight: 700;
  color: #2c3e50;
}

.blocked-note {
  font-size: 0.75rem;
  color: #c0392b;
}

.total-line {
  font-size: 0.9rem;
  color: #5a6d5a;
  text-align: center;
}

.total-line.warn {
  color: #c0392b;
  font-weight: 600;
}

.total-hint {
  font-size: 0.8rem;
}

/* Op tablet staan titel en troef naast elkaar en staan de spelers in een
   raster dat bij hun aantal past (2×2 bij vier, 3×3 bij negen). */
@media (min-width: 600px) {
  .content {
    padding: 1.5rem;
    gap: 1rem;
    justify-content: safe center;
  }

  .top-row,
  .bids {
    max-width: 64rem;
  }

  .card {
    max-width: none;
    padding: 1rem 1.25rem;
    gap: 0.75rem;
    border-radius: 14px;
  }

  .top-row {
    flex-direction: row;
    align-items: stretch;
    gap: 1.5rem;
  }

  .round-head {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: left;
  }

  .title { font-size: 1.85rem; }

  .subtitle { font-size: 1.05rem; }

  .trump-card {
    flex: 1;
    min-width: 0;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
  }

  .trumps {
    flex: 1;
    flex-wrap: nowrap;
    gap: 0.5rem;
  }

  .trump-btn {
    min-height: 3.5rem;
    border-radius: 10px;
  }

  .trump-symbol { font-size: 1.5rem; }

  .trump-label { font-size: 0.7rem; }

  /* Bij smalle vakken zakt het bod onder de naam, die blijft leesbaar */
  .bid-head {
    flex-wrap: wrap;
    gap: 0.4rem 0.65rem;
  }

  .bid-order {
    width: 1.85rem;
    height: 1.85rem;
    font-size: 0.8rem;
  }

  .bid-name { font-size: 1.3rem; }

  .dealer-tag {
    font-size: 0.7rem;
    padding: 0.2rem 0.55rem;
  }

  .bid-value { font-size: 1.5rem; }

  .blocked-note { font-size: 0.85rem; }

  .total-line { font-size: 1.05rem; }

  .total-hint { font-size: 0.95rem; }
}

@media (hover: hover) {
  .trump-btn:hover:not(.selected) { border-color: #7a9a7a; }
}
</style>
